/**
 * 🛡️ G2GENTE - Mobile Profile Edit Page
 * Edição completa do perfil do candidato/funcionário com todas as seções
 * Suporta tanto role CANDIDATE quanto EMPLOYEE
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { candidatesAPI, cnpjAPI, employeeSelfAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { 
  ArrowLeft,
  Loader2,
  User,
  Briefcase,
  GraduationCap,
  Award,
  Heart,
  MapPin,
  Mail,
  Phone,
  Building,
  Plus,
  Trash2,
  Save,
  Clock,
  X,
  Camera,
  FileText,
  Upload,
  Download,
  Search,
  CheckCircle2,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import { PartnerOriginSelect } from '../../components/candidates/PartnerOriginSelect';
import { CvAutofillDialog } from '../../components/candidates/CvAutofillDialog';

const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const TIPOS_CONTRATO = ['CLT', 'PJ', 'Estágio', 'Aprendiz', 'Temporário', 'Freelancer'];
const NIVEIS_FORMACAO = ['Ensino Fundamental', 'Ensino Médio', 'Técnico', 'Graduação', 'Pós-graduação', 'MBA', 'Mestrado', 'Doutorado'];
const MODALIDADES_TRABALHO = ['Presencial', 'Híbrido', 'Remoto'];
const NIVEIS_CARGO = ['Estagiário', 'Junior', 'Pleno', 'Senior', 'Especialista', 'Coordenador', 'Gerente', 'Diretor', 'C-Level', 'CEO'];

// Categorias de Trabalho Eventual
const CATEGORIAS_TRABALHO_EVENTUAL = [
  { id: 'garcom', nome: 'Garçom', grupo: 'Alimentação' },
  { id: 'barman', nome: 'Barman', grupo: 'Alimentação' },
  { id: 'cozinheiro', nome: 'Cozinheiro', grupo: 'Alimentação' },
  { id: 'ajudante_cozinha', nome: 'Ajudante de Cozinha', grupo: 'Alimentação' },
  { id: 'faxineiro', nome: 'Faxineiro', grupo: 'Limpeza' },
  { id: 'auxiliar_limpeza', nome: 'Auxiliar de Limpeza', grupo: 'Limpeza' },
  { id: 'promotor', nome: 'Promotor', grupo: 'Eventos' },
  { id: 'recepcionista', nome: 'Recepcionista', grupo: 'Eventos' },
  { id: 'seguranca', nome: 'Segurança', grupo: 'Eventos' },
  { id: 'carregador', nome: 'Carregador', grupo: 'Logística' },
  { id: 'motorista', nome: 'Motorista', grupo: 'Logística' },
  { id: 'entregador', nome: 'Entregador', grupo: 'Logística' },
];

export default function MobileProfileEdit() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingCurriculo, setUploadingCurriculo] = useState(false);
  const [cvSuggestions, setCvSuggestions] = useState(null);
  const handleCvApplied = (applied) => setFormData((prev) => ({ ...prev, ...applied }));
  const [uploadingCertificado, setUploadingCertificado] = useState(null); // Index of curso being uploaded
  
  // File input refs
  const photoInputRef = useRef(null);
  const curriculoInputRef = useRef(null);
  const certificadoInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // Dados Pessoais
    nome: '',
    cpf: '',
    telefone: '',
    data_nascimento: '',
    
    // Endereço
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    localizacao: '',
    
    // Perfil Profissional
    cargo: '',  // Legacy single cargo
    cargos: [], // New multiple cargos [{titulo, nivel, principal}]
    experiencia: '',
    resumo: '',
    competencias: [],
    experiencias: [],
    formacoes: [],
    cursos: [],
    
    // Situação de Emprego
    esta_trabalhando: false,
    cnpj_empresa_atual: '',
    empresa_atual: null,
    aberto_a_oportunidades: true,
    
    // Inclusão
    is_pcd: false,
    is_50_plus: false,
    consent_share: false,
    observacoes_inclusao: '',
    
    // Estagiário
    is_estagiario: false,
    entidade_cedente_estagio: '',
    curso_estagio: '',
    previsao_formatura: '',
    
    // Aprendiz
    is_aprendiz: false,
    entidade_aprendiz: '',
    programa_aprendiz: '',
    
    // Disponibilidade
    tipo_contrato_desejado: [],
    disponibilidade_mudanca: false,
    disponibilidade_remoto: '',
    pretensao_salarial: '',
    partner_id: null,
    
    // Trabalho Eventual
    disponivel_eventual: false,
    categorias_eventual: [],
    atividade_eventual_outra: '',
    
    // Files
    foto_url: '',
    curriculo_url: '',
    curriculo_nome: ''
  });
  
  // Temp inputs
  const [newCompetencia, setNewCompetencia] = useState('');
  
  // CNPJ lookup states
  const [lookingUpCnpj, setLookingUpCnpj] = useState(false);
  const [cnpjInput, setCnpjInput] = useState('');
  
  // CEP lookup states
  const [lookingUpCep, setLookingUpCep] = useState(false);
  const [cepInput, setCepInput] = useState('');

  useEffect(() => {
    // Detectar se é funcionário
    const userRole = user?.role?.toUpperCase();
    setIsEmployee(userRole === 'EMPLOYEE');
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const userRole = user?.role?.toUpperCase();
      let data;
      
      if (userRole === 'EMPLOYEE') {
        // Funcionário - usar employeeSelfAPI
        const response = await employeeSelfAPI.getMe();
        data = response.data;
      } else {
        // Candidato - usar candidatesAPI
        const response = await candidatesAPI.getMe();
        data = response.data;
      }
      
      setFormData({
        nome: data.nome || '',
        cpf: data.cpf || '',
        telefone: data.telefone || '',
        email: data.email || '',
        data_nascimento: data.data_nascimento || '',
        // Endereço
        cep: data.cep || '',
        logradouro: data.logradouro || '',
        numero: data.numero || '',
        complemento: data.complemento || '',
        bairro: data.bairro || '',
        cidade: data.cidade || '',
        estado: data.estado || '',
        localizacao: data.localizacao || '',
        // Profissional
        cargo: data.cargo || '',
        cargos: data.cargos || [],
        experiencia: data.experiencia || '',
        resumo: data.resumo || data.objetivo_profissional || '',
        competencias: data.competencias || data.habilidades || [],
        experiencias: data.experiencias || [],
        formacoes: data.formacoes || data.formacao || [],
        cursos: data.cursos || [],
        // Situação de emprego
        esta_trabalhando: data.esta_trabalhando || false,
        cnpj_empresa_atual: data.cnpj_empresa_atual || '',
        empresa_atual: data.empresa_atual || null,
        aberto_a_oportunidades: data.aberto_a_oportunidades !== false, // Default true
        // Inclusão
        is_pcd: data.is_pcd || data.pcd || false,
        is_50_plus: data.is_50_plus || false,
        consent_share: data.consent_share || false,
        observacoes_inclusao: data.observacoes_inclusao || data.tipo_deficiencia || '',
        // Estagiário fields
        is_estagiario: data.is_estagiario || false,
        entidade_cedente_estagio: data.entidade_cedente_estagio || '',
        curso_estagio: data.curso_estagio || '',
        previsao_formatura: data.previsao_formatura || '',
        // Aprendiz fields
        is_aprendiz: data.is_aprendiz || false,
        entidade_aprendiz: data.entidade_aprendiz || '',
        programa_aprendiz: data.programa_aprendiz || '',
        // Disponibilidade
        tipo_contrato_desejado: data.tipo_contrato_desejado || [],
        disponibilidade_mudanca: data.disponibilidade_mudanca || false,
        disponibilidade_remoto: data.disponibilidade_remoto || data.modalidade_trabalho || '',
        pretensao_salarial: data.pretensao_salarial || '',
        partner_id: data.partner_id || null,
        // Trabalho Eventual
        disponivel_eventual: data.disponivel_eventual || false,
        categorias_eventual: data.categorias_eventual || [],
        atividade_eventual_outra: data.atividade_eventual_outra || '',
        // Files
        foto_url: data.foto_url || '',
        curriculo_url: data.curriculo_url || '',
        curriculo_nome: data.curriculo_nome || '',
        // URLs extras
        linkedin_url: data.linkedin_url || '',
        portfolio_url: data.portfolio_url || ''
      });
      if (userRole !== 'EMPLOYEE') {
        candidatesAPI.getCvAutofill().then((r) => r.data.autofill && setCvSuggestions(r.data.autofill)).catch(() => {});
      }
      // Set CNPJ input if exists
      if (data.cnpj_empresa_atual) {
        setCnpjInput(formatCNPJ(data.cnpj_empresa_atual));
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setIsNew(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Photo upload handler
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Use apenas JPG, PNG ou WebP');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 5MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const response = await candidatesAPI.uploadPhoto(file);
      setFormData(prev => ({ ...prev, foto_url: response.data.foto_url }));
      toast.success('Foto enviada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Curriculum upload handler
  const handleCurriculoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Use apenas arquivos PDF');
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 10MB');
      return;
    }
    
    setUploadingCurriculo(true);
    try {
      const response = await candidatesAPI.uploadCurriculo(file);
      setFormData(prev => ({ 
        ...prev, 
        curriculo_url: response.data.curriculo_url,
        curriculo_nome: response.data.curriculo_nome 
      }));
      toast.success('Currículo enviado com sucesso!');
      if (response.data.autofill) setCvSuggestions(response.data.autofill);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar currículo');
    } finally {
      setUploadingCurriculo(false);
    }
  };

  // Delete photo
  const handleDeletePhoto = async () => {
    try {
      await candidatesAPI.deletePhoto();
      setFormData(prev => ({ ...prev, foto_url: '' }));
      toast.success('Foto removida');
    } catch (error) {
      toast.error('Erro ao remover foto');
    }
  };

  // Delete curriculum
  const handleDeleteCurriculo = async () => {
    try {
      await candidatesAPI.deleteCurriculo();
      setFormData(prev => ({ ...prev, curriculo_url: '', curriculo_nome: '' }));
      toast.success('Currículo removido');
    } catch (error) {
      toast.error('Erro ao remover currículo');
    }
  };

  // Format CNPJ helper
  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .slice(0, 18);
  };

  // Format CPF helper
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1-$2')
      .slice(0, 14);
  };

  // Format CEP helper
  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d)/, '$1-$2').slice(0, 9);
  };

  // Lookup address by CEP using ViaCEP
  const handleLookupCep = async () => {
    const cleanCep = cepInput.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      toast.error('CEP deve ter 8 dígitos');
      return;
    }

    setLookingUpCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        cep: cleanCep,
        logradouro: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        estado: data.uf || '',
        localizacao: `${data.localidade}/${data.uf}`
      }));
      toast.success('Endereço encontrado!');
    } catch (error) {
      toast.error('Erro ao buscar CEP');
    } finally {
      setLookingUpCep(false);
    }
  };

  // Lookup company by CNPJ
  const handleLookupCnpj = async () => {
    const cleanCnpj = cnpjInput.replace(/\D/g, '');
    if (cleanCnpj.length !== 14) {
      toast.error('CNPJ deve ter 14 dígitos');
      return;
    }

    setLookingUpCnpj(true);
    try {
      const response = await cnpjAPI.lookup(cleanCnpj);
      const companyData = response.data;
      
      // Store company info
      setFormData(prev => ({
        ...prev,
        cnpj_empresa_atual: cleanCnpj,
        empresa_atual: {
          cnpj: cleanCnpj,
          nome: companyData.nome_fantasia || companyData.razao_social,
          razao_social: companyData.razao_social,
          endereco: companyData.endereco 
            ? `${companyData.endereco.logradouro}, ${companyData.endereco.numero} - ${companyData.endereco.municipio}/${companyData.endereco.uf}`
            : null
        }
      }));
      toast.success('Empresa encontrada!');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('CNPJ não encontrado na Receita Federal');
      } else {
        toast.error('Erro ao buscar CNPJ');
      }
      // Clear company data on error
      setFormData(prev => ({
        ...prev,
        cnpj_empresa_atual: '',
        empresa_atual: null
      }));
    } finally {
      setLookingUpCnpj(false);
    }
  };

  // Clear current company
  const handleClearEmpresaAtual = () => {
    setCnpjInput('');
    setFormData(prev => ({
      ...prev,
      esta_trabalhando: false,
      cnpj_empresa_atual: '',
      empresa_atual: null
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddCompetencia = () => {
    if (newCompetencia.trim() && !formData.competencias.includes(newCompetencia.trim())) {
      setFormData(prev => ({
        ...prev,
        competencias: [...prev.competencias, newCompetencia.trim()]
      }));
      setNewCompetencia('');
    }
  };

  const handleRemoveCompetencia = (comp) => {
    setFormData(prev => ({
      ...prev,
      competencias: prev.competencias.filter(c => c !== comp)
    }));
  };

  const handleToggleContrato = (tipo) => {
    setFormData(prev => ({
      ...prev,
      tipo_contrato_desejado: prev.tipo_contrato_desejado.includes(tipo)
        ? prev.tipo_contrato_desejado.filter(t => t !== tipo)
        : [...prev.tipo_contrato_desejado, tipo]
    }));
  };

  // Experiência handlers
  const handleAddExperiencia = () => {
    setFormData(prev => ({
      ...prev,
      experiencias: [...prev.experiencias, {
        empresa: '',
        cargo: '',
        periodo_inicio: '',
        periodo_fim: '',
        atual: false,
        descricao: ''
      }]
    }));
  };

  const handleUpdateExperiencia = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      experiencias: prev.experiencias.map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const handleRemoveExperiencia = (index) => {
    setFormData(prev => ({
      ...prev,
      experiencias: prev.experiencias.filter((_, i) => i !== index)
    }));
  };

  // Formação handlers
  const handleAddFormacao = () => {
    setFormData(prev => ({
      ...prev,
      formacoes: [...prev.formacoes, {
        instituicao: '',
        curso: '',
        nivel: '',
        ano_conclusao: '',
        em_andamento: false
      }]
    }));
  };

  const handleUpdateFormacao = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      formacoes: prev.formacoes.map((form, i) => 
        i === index ? { ...form, [field]: value } : form
      )
    }));
  };

  const handleRemoveFormacao = (index) => {
    setFormData(prev => ({
      ...prev,
      formacoes: prev.formacoes.filter((_, i) => i !== index)
    }));
  };

  // Curso handlers
  const handleAddCurso = () => {
    setFormData(prev => ({
      ...prev,
      cursos: [...prev.cursos, {
        nome: '',
        instituicao: '',
        ano: '',
        carga_horaria: '',
        certificado_url: '',
        certificado_nome: ''
      }]
    }));
  };
  
  // Certificate upload handler
  const handleCertificadoUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'application/pdf' && !file.type.startsWith('image/')) {
      toast.error('Use apenas PDF ou imagens (JPG, PNG)');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 5MB');
      return;
    }
    
    setUploadingCertificado(index);
    try {
      const response = await candidatesAPI.uploadCertificado(file);
      setFormData(prev => ({
        ...prev,
        cursos: prev.cursos.map((curso, i) => 
          i === index ? { 
            ...curso, 
            certificado_url: response.data.certificado_url,
            certificado_nome: file.name 
          } : curso
        )
      }));
      toast.success('Certificado enviado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar certificado');
    } finally {
      setUploadingCertificado(null);
    }
  };
  
  const handleRemoveCertificado = (index) => {
    setFormData(prev => ({
      ...prev,
      cursos: prev.cursos.map((curso, i) => 
        i === index ? { ...curso, certificado_url: '', certificado_nome: '' } : curso
      )
    }));
  };

  const handleUpdateCurso = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      cursos: prev.cursos.map((curso, i) => 
        i === index ? { ...curso, [field]: value } : curso
      )
    }));
  };

  const handleRemoveCurso = (index) => {
    setFormData(prev => ({
      ...prev,
      cursos: prev.cursos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.cargo.trim() && formData.cargos.length === 0) {
      toast.error('Cargo pretendido é obrigatório');
      return;
    }
    
    // Validate CNPJ when working
    if (formData.esta_trabalhando && !formData.cnpj_empresa_atual) {
      toast.error('CNPJ da empresa atual é obrigatório quando está trabalhando');
      return;
    }
    
    // Validate intern fields if Estágio selected
    if (formData.tipo_contrato_desejado.includes('Estágio')) {
      if (!formData.entidade_cedente_estagio?.trim()) {
        toast.error('Instituição de Ensino é obrigatória para estágio');
        return;
      }
      if (!formData.curso_estagio?.trim()) {
        toast.error('Curso em andamento é obrigatório para estágio');
        return;
      }
    }
    
    // Validate apprentice fields if Aprendiz selected
    if (formData.tipo_contrato_desejado.includes('Aprendiz')) {
      if (!formData.entidade_aprendiz?.trim()) {
        toast.error('Entidade Qualificadora é obrigatória para Jovem Aprendiz');
        return;
      }
    }
    
    setSaving(true);
    try {
      // Build localizacao from cidade/estado if not set
      const localizacao = formData.cidade && formData.estado 
        ? `${formData.cidade} - ${formData.estado}` 
        : formData.localizacao;
      
      const experiencia = formData.experiencia || 
        (formData.experiencias.length > 0 
          ? formData.experiencias.map(e => `${e.cargo} em ${e.empresa}`).join('; ')
          : 'Não informada');
      
      // Build data object, only including non-empty values
      const dataToSave = {};
      
      // Critical fields - always include if they have value
      if (formData.nome?.trim()) dataToSave.nome = formData.nome.trim();
      if (formData.cpf?.trim()) dataToSave.cpf = formData.cpf.replace(/\D/g, '');
      if (formData.telefone?.trim()) dataToSave.telefone = formData.telefone.trim();
      if (formData.data_nascimento) dataToSave.data_nascimento = formData.data_nascimento;
      
      // Address fields - use cepInput if available (user typed but didn't search), otherwise formData.cep
      const cepToSave = cepInput?.replace(/\D/g, '') || formData.cep?.replace(/\D/g, '');
      if (cepToSave && cepToSave.length === 8) dataToSave.cep = cepToSave;
      if (formData.logradouro?.trim()) dataToSave.logradouro = formData.logradouro.trim();
      if (formData.numero?.trim()) dataToSave.numero = formData.numero.trim();
      if (formData.complemento?.trim()) dataToSave.complemento = formData.complemento.trim();
      if (formData.bairro?.trim()) dataToSave.bairro = formData.bairro.trim();
      if (formData.cidade?.trim()) dataToSave.cidade = formData.cidade.trim();
      if (formData.estado?.trim()) dataToSave.estado = formData.estado.trim();
      if (localizacao) dataToSave.localizacao = localizacao;
      
      // Professional fields
      if (formData.cargo?.trim()) dataToSave.cargo = formData.cargo.trim();
      if (formData.cargos?.length > 0) dataToSave.cargos = formData.cargos;
      if (experiencia) dataToSave.experiencia = experiencia;
      if (formData.resumo?.trim()) dataToSave.resumo = formData.resumo.trim();
      if (formData.competencias?.length > 0) dataToSave.competencias = formData.competencias;
      if (formData.experiencias?.length > 0) dataToSave.experiencias = formData.experiencias;
      if (formData.formacoes?.length > 0) dataToSave.formacoes = formData.formacoes;
      if (formData.cursos?.length > 0) dataToSave.cursos = formData.cursos;
      
      // Employment status
      dataToSave.esta_trabalhando = formData.esta_trabalhando || false;
      if (formData.cnpj_empresa_atual) dataToSave.cnpj_empresa_atual = formData.cnpj_empresa_atual;
      if (formData.empresa_atual) dataToSave.empresa_atual = formData.empresa_atual;
      dataToSave.aberto_a_oportunidades = formData.aberto_a_oportunidades !== false;
      
      // Inclusion flags
      dataToSave.is_pcd = formData.is_pcd || false;
      dataToSave.is_50_plus = formData.is_50_plus || false;
      dataToSave.consent_share = formData.consent_share || false;
      if (formData.observacoes_inclusao?.trim()) dataToSave.observacoes_inclusao = formData.observacoes_inclusao.trim();
      
      // Contract preferences
      if (formData.tipo_contrato_desejado?.length > 0) dataToSave.tipo_contrato_desejado = formData.tipo_contrato_desejado;
      dataToSave.disponibilidade_mudanca = formData.disponibilidade_mudanca || false;
      if (formData.disponibilidade_remoto) dataToSave.disponibilidade_remoto = formData.disponibilidade_remoto;
      if (formData.pretensao_salarial?.trim()) dataToSave.pretensao_salarial = formData.pretensao_salarial.trim();
      if (!isEmployee) dataToSave.partner_id = formData.partner_id || null;
      
      // Intern/Apprentice fields
      dataToSave.is_estagiario = formData.tipo_contrato_desejado?.includes('Estágio') || false;
      dataToSave.is_aprendiz = formData.tipo_contrato_desejado?.includes('Aprendiz') || false;
      if (formData.entidade_cedente_estagio?.trim()) dataToSave.entidade_cedente_estagio = formData.entidade_cedente_estagio.trim();
      if (formData.curso_estagio?.trim()) dataToSave.curso_estagio = formData.curso_estagio.trim();
      if (formData.previsao_formatura?.trim()) dataToSave.previsao_formatura = formData.previsao_formatura.trim();
      if (formData.entidade_aprendiz?.trim()) dataToSave.entidade_aprendiz = formData.entidade_aprendiz.trim();
      if (formData.programa_aprendiz?.trim()) dataToSave.programa_aprendiz = formData.programa_aprendiz.trim();
      
      // Eventual work
      dataToSave.disponivel_eventual = formData.disponivel_eventual || false;
      if (formData.categorias_eventual?.length > 0) dataToSave.categorias_eventual = formData.categorias_eventual;
      if (formData.atividade_eventual_outra?.trim()) dataToSave.atividade_eventual_outra = formData.atividade_eventual_outra.trim();
      
      // URLs extras
      if (formData.linkedin_url?.trim()) dataToSave.linkedin_url = formData.linkedin_url.trim();
      if (formData.portfolio_url?.trim()) dataToSave.portfolio_url = formData.portfolio_url.trim();
      if (formData.email?.trim()) dataToSave.email = formData.email.trim();
      
      // Files (for employees)
      if (formData.foto_url) dataToSave.foto_url = formData.foto_url;
      if (formData.curriculo_url) dataToSave.curriculo_url = formData.curriculo_url;
      
      // Determinar qual API usar baseado no tipo de usuário
      const userRole = user?.role?.toUpperCase();
      
      if (userRole === 'EMPLOYEE') {
        // Funcionário - usar employeeSelfAPI
        // Mapear campos para o formato do backend de funcionários
        const employeeData = {
          email: dataToSave.email,
          telefone: dataToSave.telefone,
          foto_url: dataToSave.foto_url,
          curriculo_url: dataToSave.curriculo_url,
          experiencias: dataToSave.experiencias,
          formacao: dataToSave.formacoes,
          cursos: dataToSave.cursos,
          habilidades: dataToSave.competencias,
          objetivo_profissional: dataToSave.resumo,
          pretensao_salarial: dataToSave.pretensao_salarial,
          modalidade_trabalho: dataToSave.disponibilidade_remoto,
          pcd: dataToSave.is_pcd,
          tipo_deficiencia: dataToSave.observacoes_inclusao,
          linkedin_url: dataToSave.linkedin_url,
          portfolio_url: dataToSave.portfolio_url
        };
        
        await employeeSelfAPI.updateMe(employeeData);
        toast.success('Perfil atualizado com sucesso!');
        navigate('/m/employee/profile');
      } else {
        // Candidato - usar candidatesAPI
        if (isNew) {
          await candidatesAPI.create(dataToSave);
          toast.success('Perfil criado com sucesso!');
        } else {
          await candidatesAPI.updateMe(dataToSave);
          toast.success('Perfil atualizado com sucesso!');
        }
        navigate('/m/profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="mobile-profile-edit">
      <CvAutofillDialog suggestions={cvSuggestions} onClose={() => setCvSuggestions(null)} onApplied={handleCvApplied} />
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-slate-600">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold text-slate-900">
              {isNew ? 'Criar Perfil' : 'Editar Perfil'}
            </h1>
          </div>
          <Button 
            onClick={handleSubmit}
            disabled={saving}
            className="bg-cyan-600 hover:bg-cyan-700"
            size="sm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Salvar
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        
        {/* FOTO E CURRÍCULO */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="w-5 h-5 text-cyan-600" />
              Foto e Currículo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Photo Upload */}
            <div>
              <Label className="mb-2 block">Foto de Perfil</Label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  {formData.foto_url ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${formData.foto_url}`}
                      alt="Foto" 
                      className="w-20 h-20 rounded-full object-cover border-2 border-slate-200"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center border-2 border-dashed border-slate-300">
                      <User className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                  {uploadingPhoto && (
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="w-full"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {formData.foto_url ? 'Trocar Foto' : 'Enviar Foto'}
                  </Button>
                  {formData.foto_url && (
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="sm"
                      onClick={handleDeletePhoto}
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remover
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">JPG, PNG ou WebP. Máximo 5MB.</p>
            </div>
            
            <Separator />
            
            {/* Curriculum Upload */}
            <div>
              <Label className="mb-2 block">Currículo (PDF)</Label>
              <input
                ref={curriculoInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleCurriculoUpload}
                className="hidden"
                data-testid="curriculo-file-input"
              />
              {/* Hidden input for certificate uploads */}
              <input
                ref={certificadoInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={(e) => {
                  const index = parseInt(certificadoInputRef.current.dataset.index);
                  handleCertificadoUpload(e, index);
                }}
                className="hidden"
              />
              
              {formData.curriculo_url ? (
                <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{formData.curriculo_nome || 'curriculo.pdf'}</p>
                      <p className="text-xs text-slate-500">PDF</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => curriculoInputRef.current?.click()}
                      className="p-2 text-cyan-600 hover:bg-cyan-50 rounded-full"
                      disabled={uploadingCurriculo}
                    >
                      {uploadingCurriculo ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteCurriculo}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => curriculoInputRef.current?.click()}
                  disabled={uploadingCurriculo}
                  className="w-full h-20 border-dashed"
                >
                  {uploadingCurriculo ? (
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  ) : (
                    <FileText className="w-6 h-6 mr-2 text-slate-400" />
                  )}
                  <span className="text-slate-600">{uploadingCurriculo ? 'Enviando e lendo seu currículo...' : 'Clique para enviar seu currículo'}</span>
                </Button>
              )}
              <p className="text-xs text-slate-500 mt-2">Apenas arquivos PDF. Máximo 10MB.</p>
            </div>
          </CardContent>
        </Card>
        
        {/* 1. DADOS PESSOAIS */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-5 h-5 text-cyan-600" />
              Dados Pessoais
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome Completo *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => handleChange('nome', e.target.value)}
                placeholder="Seu nome completo"
              />
            </div>
            
            <div>
              <Label htmlFor="cpf">CPF *</Label>
              <Input
                id="cpf"
                value={formatCPF(formData.cpf || '')}
                onChange={(e) => handleChange('cpf', e.target.value.replace(/\D/g, ''))}
                placeholder="000.000.000-00"
                maxLength={14}
              />
            </div>
            
            <div>
              <Label htmlFor="telefone">Celular *</Label>
              <Input
                id="telefone"
                value={formData.telefone}
                onChange={(e) => handleChange('telefone', e.target.value)}
                placeholder="(11) 99999-9999"
              />
            </div>
            
            <div>
              <Label htmlFor="data_nascimento">Data de Nascimento</Label>
              <Input
                id="data_nascimento"
                type="date"
                value={formData.data_nascimento}
                onChange={(e) => handleChange('data_nascimento', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* 1.1. ENDEREÇO */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-5 h-5 text-cyan-600" />
              Endereço
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* CEP com busca */}
            <div>
              <Label htmlFor="cep">CEP</Label>
              <div className="flex gap-2">
                <Input
                  id="cep"
                  value={formatCEP(cepInput || formData.cep || '')}
                  onChange={(e) => setCepInput(formatCEP(e.target.value))}
                  placeholder="00000-000"
                  maxLength={9}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleLookupCep}
                  disabled={lookingUpCep}
                >
                  {lookingUpCep ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Digite o CEP e clique na lupa para buscar</p>
            </div>

            <div>
              <Label htmlFor="logradouro">Rua/Avenida</Label>
              <Input
                id="logradouro"
                value={formData.logradouro}
                onChange={(e) => handleChange('logradouro', e.target.value)}
                placeholder="Rua das Flores"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="numero">Número</Label>
                <Input
                  id="numero"
                  value={formData.numero}
                  onChange={(e) => handleChange('numero', e.target.value)}
                  placeholder="123"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="complemento">Complemento</Label>
                <Input
                  id="complemento"
                  value={formData.complemento}
                  onChange={(e) => handleChange('complemento', e.target.value)}
                  placeholder="Apto 42"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="bairro">Bairro</Label>
              <Input
                id="bairro"
                value={formData.bairro}
                onChange={(e) => handleChange('bairro', e.target.value)}
                placeholder="Centro"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cidade">Cidade</Label>
                <Input
                  id="cidade"
                  value={formData.cidade}
                  onChange={(e) => handleChange('cidade', e.target.value)}
                  placeholder="São Paulo"
                />
              </div>
              <div>
                <Label htmlFor="estado">Estado</Label>
                <select
                  id="estado"
                  value={formData.estado}
                  onChange={(e) => handleChange('estado', e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">UF</option>
                  {ESTADOS_BR.map(uf => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. PERFIL PROFISSIONAL */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-cyan-600" />
              Perfil Profissional
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Múltiplos Cargos Pretendidos */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  Cargos Pretendidos
                </Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const newCargo = { titulo: '', nivel: 'Pleno', principal: formData.cargos.length === 0 };
                    handleChange('cargos', [...formData.cargos, newCargo]);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              
              <p className="text-xs text-slate-500 mb-3">
                Adicione os cargos que deseja ocupar. O cargo de maior nível será usado como referência para o teste DISC.
              </p>
              
              {formData.cargos.length === 0 ? (
                <div>
                  <Label htmlFor="cargo">Cargo Pretendido *</Label>
                  <Input
                    id="cargo"
                    value={formData.cargo}
                    onChange={(e) => handleChange('cargo', e.target.value)}
                    placeholder="Ex: Analista de Marketing"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.cargos.map((cargo, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-3 relative">
                      <button
                        type="button"
                        onClick={() => {
                          const newCargos = formData.cargos.filter((_, i) => i !== index);
                          // Ensure first cargo is principal if deleting the principal one
                          if (cargo.principal && newCargos.length > 0) {
                            newCargos[0].principal = true;
                          }
                          handleChange('cargos', newCargos);
                        }}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      
                      <div className="flex items-start gap-3 pr-8">
                        <div className="flex-1">
                          <Input
                            value={cargo.titulo}
                            onChange={(e) => {
                              const newCargos = [...formData.cargos];
                              newCargos[index].titulo = e.target.value;
                              handleChange('cargos', newCargos);
                            }}
                            placeholder="Ex: Analista de Marketing"
                            className="mb-2"
                          />
                          <div className="flex items-center gap-2">
                            <select
                              value={cargo.nivel}
                              onChange={(e) => {
                                const newCargos = [...formData.cargos];
                                newCargos[index].nivel = e.target.value;
                                handleChange('cargos', newCargos);
                              }}
                              className="flex-1 h-9 text-sm rounded-md border border-input bg-background px-3"
                            >
                              {NIVEIS_CARGO.map(nivel => (
                                <option key={nivel} value={nivel}>{nivel}</option>
                              ))}
                            </select>
                            <label className="flex items-center gap-1 text-xs text-slate-600 whitespace-nowrap">
                              <input
                                type="radio"
                                name="cargo-principal"
                                checked={cargo.principal}
                                onChange={() => {
                                  const newCargos = formData.cargos.map((c, i) => ({
                                    ...c,
                                    principal: i === index
                                  }));
                                  handleChange('cargos', newCargos);
                                }}
                                className="w-3 h-3"
                              />
                              Principal
                            </label>
                          </div>
                        </div>
                      </div>
                      {cargo.principal && (
                        <Badge className="mt-2 bg-cyan-100 text-cyan-700">
                          Usado para DISC
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <Label htmlFor="resumo">Resumo Profissional</Label>
              <Textarea
                id="resumo"
                value={formData.resumo}
                onChange={(e) => handleChange('resumo', e.target.value)}
                placeholder="Breve descrição sobre você e sua carreira..."
                rows={3}
              />
            </div>

            <Separator />
            
            {/* Experiências */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-1">
                  <Building className="w-4 h-4" />
                  Experiências Profissionais
                </Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={handleAddExperiencia}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              
              {formData.experiencias.length === 0 ? (
                <div>
                  <Textarea
                    value={formData.experiencia}
                    onChange={(e) => handleChange('experiencia', e.target.value)}
                    placeholder="Descreva sua experiência profissional ou adicione itens acima..."
                    rows={2}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.experiencias.map((exp, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-4 relative">
                      <button
                        type="button"
                        onClick={() => handleRemoveExperiencia(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={exp.empresa}
                            onChange={(e) => handleUpdateExperiencia(index, 'empresa', e.target.value)}
                            placeholder="Empresa"
                          />
                          <Input
                            value={exp.cargo}
                            onChange={(e) => handleUpdateExperiencia(index, 'cargo', e.target.value)}
                            placeholder="Cargo"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={exp.periodo_inicio}
                            onChange={(e) => handleUpdateExperiencia(index, 'periodo_inicio', e.target.value)}
                            placeholder="Início (MM/AAAA)"
                          />
                          <Input
                            value={exp.periodo_fim}
                            onChange={(e) => handleUpdateExperiencia(index, 'periodo_fim', e.target.value)}
                            placeholder="Fim (MM/AAAA)"
                            disabled={exp.atual}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={exp.atual}
                            onCheckedChange={(v) => handleUpdateExperiencia(index, 'atual', v)}
                          />
                          <Label className="text-sm">Emprego atual</Label>
                        </div>
                        <Textarea
                          value={exp.descricao}
                          onChange={(e) => handleUpdateExperiencia(index, 'descricao', e.target.value)}
                          placeholder="Descrição das atividades..."
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />
            
            {/* Formações */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-1">
                  <GraduationCap className="w-4 h-4" />
                  Formação Acadêmica
                </Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={handleAddFormacao}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              
              {formData.formacoes.length > 0 && (
                <div className="space-y-4">
                  {formData.formacoes.map((form, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-4 relative">
                      <button
                        type="button"
                        onClick={() => handleRemoveFormacao(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      
                      <div className="space-y-3">
                        <Input
                          value={form.curso}
                          onChange={(e) => handleUpdateFormacao(index, 'curso', e.target.value)}
                          placeholder="Nome do curso"
                        />
                        <Input
                          value={form.instituicao}
                          onChange={(e) => handleUpdateFormacao(index, 'instituicao', e.target.value)}
                          placeholder="Instituição"
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <select
                            value={form.nivel}
                            onChange={(e) => handleUpdateFormacao(index, 'nivel', e.target.value)}
                            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            <option value="">Nível</option>
                            {NIVEIS_FORMACAO.map(n => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                          <Input
                            value={form.ano_conclusao}
                            onChange={(e) => handleUpdateFormacao(index, 'ano_conclusao', e.target.value)}
                            placeholder="Ano conclusão"
                            disabled={form.em_andamento}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={form.em_andamento}
                            onCheckedChange={(v) => handleUpdateFormacao(index, 'em_andamento', v)}
                          />
                          <Label className="text-sm">Em andamento</Label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />
            
            {/* Cursos */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-1">
                  <Award className="w-4 h-4" />
                  Cursos e Certificados
                </Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={handleAddCurso}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              
              <p className="text-xs text-slate-500 mb-3">
                Adicione seus cursos e anexe os certificados para validação.
              </p>
              
              {formData.cursos.length > 0 && (
                <div className="space-y-4">
                  {formData.cursos.map((curso, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-4 relative">
                      <button
                        type="button"
                        onClick={() => handleRemoveCurso(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      
                      <div className="space-y-3">
                        <Input
                          value={curso.nome}
                          onChange={(e) => handleUpdateCurso(index, 'nome', e.target.value)}
                          placeholder="Nome do curso"
                          data-testid={`curso-nome-${index}`}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={curso.instituicao}
                            onChange={(e) => handleUpdateCurso(index, 'instituicao', e.target.value)}
                            placeholder="Instituição"
                          />
                          <Input
                            value={curso.ano}
                            onChange={(e) => handleUpdateCurso(index, 'ano', e.target.value)}
                            placeholder="Ano"
                          />
                        </div>
                        <Input
                          value={curso.carga_horaria || ''}
                          onChange={(e) => handleUpdateCurso(index, 'carga_horaria', e.target.value)}
                          placeholder="Carga horária (ex: 40h)"
                        />
                        
                        {/* Certificate Upload */}
                        <div className="pt-2 border-t border-slate-200">
                          <Label className="text-xs text-slate-600 mb-2 block">
                            Certificado (opcional)
                          </Label>
                          {curso.certificado_url ? (
                            <div className="flex items-center justify-between bg-green-50 rounded-lg p-3 border border-green-200">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-green-700 truncate">
                                    {curso.certificado_nome || 'Certificado'}
                                  </p>
                                  <p className="text-xs text-green-600">Anexado</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <a
                                  href={`${process.env.REACT_APP_BACKEND_URL}${curso.certificado_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 text-green-600 hover:bg-green-100 rounded-full"
                                >
                                  <Download className="w-4 h-4" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveCertificado(index)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <Button 
                              type="button"
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                certificadoInputRef.current.dataset.index = index;
                                certificadoInputRef.current?.click();
                              }}
                              disabled={uploadingCertificado === index}
                              className="w-full h-12 border-dashed"
                              data-testid={`upload-certificado-${index}`}
                            >
                              {uploadingCertificado === index ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Upload className="w-4 h-4 mr-2" />
                              )}
                              <span className="text-slate-600">Anexar Certificado</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />
            
            {/* Competências */}
            <div>
              <Label className="mb-2 block">Competências Técnicas</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newCompetencia}
                  onChange={(e) => setNewCompetencia(e.target.value)}
                  placeholder="Ex: Excel, Python, Gestão de Projetos"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCompetencia())}
                />
                <Button type="button" variant="outline" onClick={handleAddCompetencia}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.competencias.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.competencias.map((comp, i) => (
                    <Badge 
                      key={i} 
                      variant="secondary"
                      className="bg-cyan-50 text-cyan-700 cursor-pointer hover:bg-red-50 hover:text-red-700"
                      onClick={() => handleRemoveCompetencia(comp)}
                    >
                      {comp}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 3. SITUAÇÃO DE EMPREGO */}
        <Card className="border-slate-200" data-testid="employment-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="w-5 h-5 text-cyan-600" />
              Situação de Emprego
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Está trabalhando atualmente?</Label>
                <p className="text-xs text-slate-500">Informe se você está empregado</p>
              </div>
              <Switch
                checked={formData.esta_trabalhando}
                onCheckedChange={(v) => {
                  handleChange('esta_trabalhando', v);
                  if (!v) handleClearEmpresaAtual();
                }}
                data-testid="is-employed-switch"
              />
            </div>
            
            {formData.esta_trabalhando && (
              <>
                <Separator />
                <div className="space-y-3">
                  <Label className="flex items-center gap-1">
                    Empresa onde trabalha
                    <span className="text-red-500">*</span>
                  </Label>
                  
                  {formData.empresa_atual ? (
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-green-900">{formData.empresa_atual.nome}</p>
                            <p className="text-sm text-green-700">
                              CNPJ: {formData.cnpj_empresa_atual.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}
                            </p>
                            {formData.empresa_atual.endereco && (
                              <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {formData.empresa_atual.endereco}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleClearEmpresaAtual}
                          className="text-green-700 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-green-600 mt-3">
                        Esta empresa não poderá ver seu perfil nas buscas
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={cnpjInput}
                          onChange={(e) => setCnpjInput(formatCNPJ(e.target.value))}
                          placeholder="00.000.000/0000-00"
                          className="flex-1"
                          data-testid="cnpj-input"
                        />
                        <Button
                          type="button"
                          onClick={handleLookupCnpj}
                          disabled={lookingUpCnpj || cnpjInput.replace(/\D/g, '').length !== 14}
                          className="bg-cyan-600 hover:bg-cyan-700"
                          data-testid="lookup-cnpj-btn"
                        >
                          {lookingUpCnpj ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Search className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Digite o CNPJ e clique na lupa para buscar os dados da empresa
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* 4. INCLUSÃO */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="w-5 h-5 text-cyan-600" />
              Inclusão
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Pessoa com Deficiência (PCD)</Label>
                <p className="text-xs text-slate-500">Elegível a vagas afirmativas</p>
              </div>
              <Switch
                checked={formData.is_pcd}
                onCheckedChange={(v) => handleChange('is_pcd', v)}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Profissional 50+</Label>
                <p className="text-xs text-slate-500">Elegível a políticas de inclusão etária</p>
              </div>
              <Switch
                checked={formData.is_50_plus}
                onCheckedChange={(v) => handleChange('is_50_plus', v)}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Visível para Empresas</Label>
                <p className="text-xs text-slate-500">Seu perfil aparece na busca</p>
              </div>
              <span className="text-xs font-medium text-green-700" data-testid="profile-edit-always-visible">Sempre ativo</span>
            </div>
            
            <div>
              <Label htmlFor="obs_inclusao">Observações (opcional)</Label>
              <Textarea
                id="obs_inclusao"
                value={formData.observacoes_inclusao}
                onChange={(e) => handleChange('observacoes_inclusao', e.target.value)}
                placeholder="Informações adicionais sobre necessidades ou adaptações..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* 5. TRABALHO EVENTUAL / SERVIÇOS */}
        <Card className="border-slate-200" data-testid="trabalho-eventual-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="w-5 h-5 text-cyan-600" />
              Trabalho Eventual / Serviços
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`p-4 rounded-lg border-2 ${formData.disponivel_eventual ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${formData.disponivel_eventual ? 'bg-amber-100' : 'bg-slate-200'}`}>
                    <Wrench className={`w-5 h-5 ${formData.disponivel_eventual ? 'text-amber-600' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <Label className="font-medium">Disponível para serviços eventuais?</Label>
                    <p className="text-xs text-slate-500">
                      Trabalhos por diária, bicos, eventos, etc.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.disponivel_eventual}
                  onCheckedChange={(v) => handleChange('disponivel_eventual', v)}
                  data-testid="disponivel-eventual-switch"
                />
              </div>
            </div>
            
            {formData.disponivel_eventual && (
              <>
                <Separator />
                
                <div>
                  <Label className="mb-3 block">Selecione as atividades que você realiza:</Label>
                  
                  {/* Agrupar por categoria */}
                  {['Alimentação', 'Limpeza', 'Eventos', 'Logística'].map(grupo => (
                    <div key={grupo} className="mb-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{grupo}</p>
                      <div className="flex flex-wrap gap-2">
                        {CATEGORIAS_TRABALHO_EVENTUAL.filter(c => c.grupo === grupo).map(cat => (
                          <Badge
                            key={cat.id}
                            variant={formData.categorias_eventual?.includes(cat.id) ? "default" : "outline"}
                            className={`cursor-pointer ${
                              formData.categorias_eventual?.includes(cat.id)
                                ? 'bg-amber-600 hover:bg-amber-700'
                                : 'hover:bg-slate-100'
                            }`}
                            onClick={() => {
                              const current = formData.categorias_eventual || [];
                              const updated = current.includes(cat.id)
                                ? current.filter(c => c !== cat.id)
                                : [...current, cat.id];
                              handleChange('categorias_eventual', updated);
                            }}
                            data-testid={`cat-eventual-${cat.id}`}
                          >
                            {cat.nome}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div>
                  <Label htmlFor="atividade_eventual_outra">Outra atividade (opcional)</Label>
                  <Input
                    id="atividade_eventual_outra"
                    value={formData.atividade_eventual_outra}
                    onChange={(e) => handleChange('atividade_eventual_outra', e.target.value)}
                    placeholder="Descreva outra atividade que realiza"
                    data-testid="atividade-eventual-outra-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Informe se realiza alguma atividade não listada acima
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* 6. DISPONIBILIDADE */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-600" />
              Disponibilidade
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Aberto a Oportunidades - Principal */}
            <div className="p-4 rounded-lg border-2 bg-green-50 border-green-300" data-testid="open-to-opportunities-info">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-100">
                  <Briefcase className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <Label className="font-medium">Aberto a novas oportunidades</Label>
                  <p className="text-xs text-slate-500">Você recebe vagas e aparece nas buscas das empresas</p>
                </div>
              </div>
            </div>
            
            <Separator />
            
            <div>
              <Label className="mb-2 block">Tipo de Contrato Desejado</Label>
              <div className="flex flex-wrap gap-2">
                {TIPOS_CONTRATO.map(tipo => (
                  <Badge
                    key={tipo}
                    variant={formData.tipo_contrato_desejado.includes(tipo) ? "default" : "outline"}
                    className={`cursor-pointer ${
                      formData.tipo_contrato_desejado.includes(tipo) 
                        ? 'bg-cyan-600 hover:bg-cyan-700' 
                        : 'hover:bg-slate-100'
                    }`}
                    onClick={() => handleToggleContrato(tipo)}
                  >
                    {tipo}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Campos Específicos - Estagiário */}
            {formData.tipo_contrato_desejado.includes('Estágio') && (
              <div className="bg-blue-50 rounded-lg p-4 space-y-4 border border-blue-200">
                <div className="flex items-center gap-2 text-blue-700">
                  <GraduationCap className="w-5 h-5" />
                  <span className="font-medium">Informações de Estágio</span>
                </div>
                
                <div>
                  <Label htmlFor="entidade_cedente_estagio">Instituição de Ensino *</Label>
                  <Input
                    id="entidade_cedente_estagio"
                    value={formData.entidade_cedente_estagio}
                    onChange={(e) => handleChange('entidade_cedente_estagio', e.target.value)}
                    placeholder="Ex: Universidade Federal de São Paulo"
                    data-testid="entidade-cedente-estagio-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Entidade cedente responsável pelo estágio
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="curso_estagio">Curso em Andamento *</Label>
                  <Input
                    id="curso_estagio"
                    value={formData.curso_estagio}
                    onChange={(e) => handleChange('curso_estagio', e.target.value)}
                    placeholder="Ex: Administração de Empresas"
                    data-testid="curso-estagio-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="previsao_formatura">Previsão de Formatura</Label>
                  <Input
                    id="previsao_formatura"
                    value={formData.previsao_formatura}
                    onChange={(e) => handleChange('previsao_formatura', e.target.value)}
                    placeholder="Ex: 12/2025"
                    data-testid="previsao-formatura-input"
                  />
                </div>
              </div>
            )}
            
            {/* Campos Específicos - Aprendiz */}
            {formData.tipo_contrato_desejado.includes('Aprendiz') && (
              <div className="bg-green-50 rounded-lg p-4 space-y-4 border border-green-200">
                <div className="flex items-center gap-2 text-green-700">
                  <Award className="w-5 h-5" />
                  <span className="font-medium">Informações de Jovem Aprendiz</span>
                </div>
                
                <div>
                  <Label htmlFor="entidade_aprendiz">Entidade Qualificadora *</Label>
                  <Input
                    id="entidade_aprendiz"
                    value={formData.entidade_aprendiz}
                    onChange={(e) => handleChange('entidade_aprendiz', e.target.value)}
                    placeholder="Ex: SENAC, SENAI, CIEE"
                    data-testid="entidade-aprendiz-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Instituição onde está matriculado no programa de aprendizagem
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="programa_aprendiz">Nome do Programa/Curso</Label>
                  <Input
                    id="programa_aprendiz"
                    value={formData.programa_aprendiz}
                    onChange={(e) => handleChange('programa_aprendiz', e.target.value)}
                    placeholder="Ex: Aprendiz em Administração"
                    data-testid="programa-aprendiz-input"
                  />
                </div>
              </div>
            )}
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Disponível para Mudança</Label>
                <p className="text-xs text-slate-500">Aceita mudar de cidade/estado</p>
              </div>
              <Switch
                checked={formData.disponibilidade_mudanca}
                onCheckedChange={(v) => handleChange('disponibilidade_mudanca', v)}
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Modalidade de Trabalho</Label>
              <div className="flex gap-2">
                {MODALIDADES_TRABALHO.map(mod => (
                  <Badge
                    key={mod}
                    variant={formData.disponibilidade_remoto === mod ? "default" : "outline"}
                    className={`cursor-pointer flex-1 justify-center ${
                      formData.disponibilidade_remoto === mod 
                        ? 'bg-cyan-600 hover:bg-cyan-700' 
                        : 'hover:bg-slate-100'
                    }`}
                    onClick={() => handleChange('disponibilidade_remoto', mod)}
                  >
                    {mod}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div>
              <Label htmlFor="pretensao">Pretensão Salarial</Label>
              <Input
                id="pretensao"
                value={formData.pretensao_salarial}
                onChange={(e) => handleChange('pretensao_salarial', e.target.value)}
                placeholder="Ex: R$ 5.000 - R$ 7.000 ou A combinar"
              />
            </div>
            {!isEmployee && (
              <PartnerOriginSelect value={formData.partner_id} onChange={(v) => handleChange('partner_id', v)} />
            )}
          </CardContent>
        </Card>

        {/* Spacer for fixed bottom button */}
        <div className="h-4"></div>
      </div>
      
      {/* Fixed Save Button at Bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 z-20 safe-area-bottom">
        <Button 
          onClick={handleSubmit}
          disabled={saving}
          className="w-full bg-cyan-600 hover:bg-cyan-700 h-12 text-base font-semibold shadow-lg"
          data-testid="save-profile-btn"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
          ) : (
            <Save className="w-5 h-5 mr-2" />
          )}
          {isNew ? 'Criar Perfil' : 'Salvar Alterações'}
        </Button>
      </div>
    </div>
  );
}
